import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDynamicFields } from './DynamicFieldsContext';
import { CustomField } from '@frontend/features/custom-fields/types';

const fieldTypes = [
  { value: 'boolean', label: 'Checkbox' },
  { value: 'date', label: 'DatePicker' },
  { value: 'file', label: 'File' },
  { value: 'select', label: 'Select' },
  { value: 'string', label: 'TextField' },
  { value: 'longtext', label: 'Textarea' },
];

export const SchemaGenerator: React.FC = () => {
  const { fields, updateFields } = useDynamicFields();
  const [fieldType, setFieldType] = useState<CustomField['fieldType']>('string');
  const [label, setLabel] = useState<string>('');
  const [name, setName] = useState<string>('');
  const [required, setRequired] = useState<boolean>(false);
  const [options, setOptions] = useState<string>('');
  const { t } = useTranslation();

  const addField = () => {
    const newField: CustomField = {
      fieldType,
      label,
      name,
      required,
      options: fieldType === 'select' ? options.split(',').map((option) => option.trim()) : undefined,
    };
    updateFields([...(fields ?? []), newField]);
    resetForm();
  };

  const resetForm = () => {
    setFieldType('string');
    setLabel('');
    setName('');
    setRequired(false);
    setOptions('');
  };

  return (
    <Box display="flex" gap={2} flexDirection="column">
      <Typography variant="subtitle1">{t('supportForms.generator.title')}</Typography>
      <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
        <Box>
          <FormControl fullWidth margin="normal">
            <InputLabel>{t('supportForms.generator.fieldType')}</InputLabel>
            <Select
              size="small"
              value={fieldType}
              onChange={(e) => setFieldType(e.target.value as CustomField['fieldType'])}
            >
              {fieldTypes.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box>
          {fieldType === 'select' && (
            <TextField
              fullWidth
              size="small"
              margin="normal"
              label={t('supportForms.generator.optionsLabel')}
              value={options}
              onChange={(e) => setOptions(e.target.value)}
            />
          )}
        </Box>
        <Box>
          <TextField
            fullWidth
            size="small"
            margin="normal"
            label={t('supportForms.generator.label')}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />
        </Box>
        <Box>
          <TextField
            fullWidth
            size="small"
            margin="normal"
            label={t('supportForms.generator.key')}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Box>

        <FormControlLabel
          control={<Checkbox checked={required} onChange={(e) => setRequired(e.target.checked)} />}
          label={t('supportForms.generator.isRequired')}
        />
        <Box>
          <Button
            variant="outlined"
            color="primary"
            onClick={addField}
            disabled={!name || !label}
            size="small"
            sx={{
              flex: 1,
            }}
          >
            {t('supportForms.generator.addField')}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
